import { Link, Outlet, createRootRouteWithContext, useMatches } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Button } from '@/components/ui/Button';

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Root-Route. Entscheidet anhand der Route-ID, ob das Layout (Sidebar+Header)
 * gerendert wird. /login wird "nackt" gerendert; alles andere ist protected.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent(): JSX.Element {
  const matches = useMatches();
  const isLogin = matches.some((m) => m.routeId === '/login');

  if (isLogin) {
    return <Outlet />;
  }

  return (
    <ProtectedRoute>
      <Layout>
        <Outlet />
      </Layout>
    </ProtectedRoute>
  );
}

/**
 * 404-Ansicht. Wird via `notFoundComponent` in den Outlet der RootComponent
 * gerendert und liegt damit für eingeloggte Nutzer bereits INNERHALB des
 * Layouts (Sidebar bleibt sichtbar). Zusätzlich ein expliziter Rückweg zum
 * Dashboard, damit die Seite keine Sackgasse ist.
 */
function NotFound(): JSX.Element {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <div>
        <p className="text-sm uppercase tracking-wider text-muted">404</p>
        <h1 className="mt-2 text-2xl font-semibold">Seite nicht gefunden</h1>
        <p className="mt-1 text-muted">Diese Route existiert (noch) nicht.</p>
      </div>
      <Link to="/">
        <Button>Zum Dashboard</Button>
      </Link>
    </div>
  );
}
